import { setAmplitudeUserProperties } from '@schooly/analytics';
import {
  GET_SCHOOL_QUERY,
  useChangeLastSchoolMutation,
  useCheckSystemRoleQuery,
  useGetAttendanceCodesQuery,
  useGetCategoriesQuery,
  useGetNotificationAttendanceSettingsQuery,
  useGetSchoolYears,
  useUpdateSchoolMutation,
} from '@schooly/api';
import { AuthContextParams, useAuth, useUserContext } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import { SchoolUserRole } from '@schooly/constants';
import { useFlag } from '@schooly/hooks/use-flag';
import { SchoolPropertiesMap, useSchoolProperties } from '@schooly/hooks/use-school-properties';
import { createContext, FC, PropsWithChildren, useEffect } from 'react';
import { useCallback, useMemo } from 'react';
import { useMatch } from 'react-router';

import { allowRenderSchoolSettings } from '../helpers/renderRoutesByPermissions';
import { SchoolTuneStatusType } from '../pages/School/SchoolTune/tabs/statuses/scheme';
import { getStatusesByParentCategory } from '../pages/School/SchoolTune/tabs/statuses/utils';
import { clearReactQueryStorageCache, queryClient } from '../queryClient';

type UseSchoolContext = {
  schoolId?: string;
  currentSchool?: AuthContextParams['currentSchool'];

  staffPropertiesMap: SchoolPropertiesMap;
  staffActivePropertiesMap: SchoolPropertiesMap;
  studentPropertiesMap: SchoolPropertiesMap;
  studentActivePropertiesMap: SchoolPropertiesMap;
  studentStatuses: SchoolTuneStatusType;
  staffStatuses: SchoolTuneStatusType;
  activeStudentStatuses: SchoolTuneStatusType;
  activeStaffStatuses: SchoolTuneStatusType;

  hasSchoolYears: boolean;
  hasStatuses: boolean;
  hasHouses: boolean;
  hasDepartments: boolean;
  hasSystemRole?: boolean;
  isSchoolAdmin: boolean;
  isFetching: boolean;
  isCompleted?: boolean;
  isSchoolPath: boolean;
  isSchoolChanging: boolean;
  updateSchoolMutation: ReturnType<typeof useUpdateSchoolMutation>;
  onSelectSchool: (v: string) => Promise<void>;

  staffPropertiesFetching: boolean;
  studentPropertiesFetching: boolean;
  categoriesFetching: boolean;
};

export const SchoolContext = createContext<UseSchoolContext>({
  schoolId: undefined,
  currentSchool: undefined,

  staffPropertiesMap: {} as SchoolPropertiesMap,
  staffActivePropertiesMap: {} as SchoolPropertiesMap,
  studentPropertiesMap: {} as SchoolPropertiesMap,
  studentActivePropertiesMap: {} as SchoolPropertiesMap,
  studentStatuses: {} as SchoolTuneStatusType,
  staffStatuses: {} as SchoolTuneStatusType,
  activeStudentStatuses: {} as SchoolTuneStatusType,
  activeStaffStatuses: {} as SchoolTuneStatusType,

  hasSchoolYears: false,
  hasStatuses: false,
  hasHouses: false,
  hasDepartments: false,
  hasSystemRole: false,
  isSchoolAdmin: false,
  isFetching: false,
  isCompleted: false,
  isSchoolPath: false,
  isSchoolChanging: false,
  updateSchoolMutation: (() => {}) as unknown as ReturnType<typeof useUpdateSchoolMutation>,
  onSelectSchool: async () => {},

  staffPropertiesFetching: false,
  studentPropertiesFetching: false,
  categoriesFetching: false,
});

export const WithSchool: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useUserContext();
  if (!user) return children;

  return <WithSchoolWhenAuthenticated>{children}</WithSchoolWhenAuthenticated>;
};

const WithSchoolWhenAuthenticated: FC<PropsWithChildren> = ({ children }) => {
  const match = useMatch('/settings/*');
  const [isSchoolChanging, startChanging, stopChanging] = useFlag(false);

  const { schoolId = '', currentSchool, permissions } = useAuth();
  const { data: hasSystemRole, isLoading: systemRoleFetching } = useCheckSystemRoleQuery(schoolId, {
    enabled: !!schoolId,
  });
  const { showError } = useNotifications();
  const changeLastSchool = useChangeLastSchoolMutation();

  const handleSelectSchool = useCallback(
    async (id: string) => {
      if (currentSchool?.id === id) return;

      startChanging();
      await changeLastSchool.mutateAsync(id, {
        onSuccess: () => {
          clearReactQueryStorageCache();
          window.location.href = '/';
        },
        onError: (e) => {
          showError(e);
          stopChanging();
        },
      });
    },
    [currentSchool?.id, startChanging, changeLastSchool, showError, stopChanging],
  );

  const updateSchoolMutation = useUpdateSchoolMutation({
    onSettled: () => {
      queryClient.invalidateQueries([GET_SCHOOL_QUERY]);
    },
  });

  const { data, isLoading: schoolYearsFetching } = useGetSchoolYears(schoolId, {
    enabled: !!schoolId,
  });

  // // PREFETCH SOME COMMON DATA that is not  //

  useGetNotificationAttendanceSettingsQuery({ schoolId }, { enabled: !!schoolId });
  useGetAttendanceCodesQuery(schoolId, { enabled: !!schoolId });

  const {
    propertiesMap: studentPropertiesMap,
    activePropertiesMap: studentActivePropertiesMap,
    isLoading: studentPropertiesFetching,
  } = useSchoolProperties(
    {
      schoolId,
      userType: SchoolUserRole.Student,
    },
    { enabled: !!schoolId },
  );

  const {
    propertiesMap: staffPropertiesMap,
    activePropertiesMap: staffActivePropertiesMap,
    isLoading: staffPropertiesFetching,
  } = useSchoolProperties(
    {
      schoolId,
      userType: SchoolUserRole.Staff,
    },
    { enabled: !!schoolId },
  );

  const { data: categoriesData, isLoading: categoriesFetching } = useGetCategoriesQuery({
    enabled: !!schoolId,
  });

  const statusesByParentCategory = useMemo(() => {
    const categories = categoriesData?.categories ?? [];
    const studentStatuses = getStatusesByParentCategory(studentPropertiesMap.status, categories);
    const staffStatuses = getStatusesByParentCategory(staffPropertiesMap.status, categories);
    const activeStudentStatuses = getStatusesByParentCategory(
      studentActivePropertiesMap.status,
      categories,
    );
    const activeStaffStatuses = getStatusesByParentCategory(
      staffActivePropertiesMap.status,
      categories,
    );

    return { studentStatuses, staffStatuses, activeStudentStatuses, activeStaffStatuses };
  }, [
    categoriesData?.categories,
    staffActivePropertiesMap.status,
    staffPropertiesMap.status,
    studentActivePropertiesMap.status,
    studentPropertiesMap.status,
  ]);

  useEffect(() => {
    if (!currentSchool) return;
    setAmplitudeUserProperties(currentSchool);
  }, [currentSchool]);

  const isSchoolPath = match != null;
  const isSchoolAdmin = allowRenderSchoolSettings(permissions);

  const hasSchoolYears = Boolean(data?.school_years.length);
  const hasStatuses =
    Boolean(studentPropertiesMap.status?.length) && Boolean(staffPropertiesMap.status?.length);

  const hasHouses = Boolean(studentPropertiesMap.house?.length);
  const hasDepartments = Boolean(studentPropertiesMap.department.length);

  const isFetching =
    schoolYearsFetching ||
    systemRoleFetching ||
    staffPropertiesFetching ||
    studentPropertiesFetching ||
    categoriesFetching;

  const isCompleted = hasSchoolYears && hasStatuses && hasSystemRole;

  return (
    <SchoolContext.Provider
      value={{
        schoolId,
        currentSchool,

        staffPropertiesMap,
        staffActivePropertiesMap,
        studentPropertiesMap,
        studentActivePropertiesMap,
        studentStatuses: statusesByParentCategory.studentStatuses,
        staffStatuses: statusesByParentCategory.staffStatuses,
        activeStudentStatuses: statusesByParentCategory.activeStudentStatuses,
        activeStaffStatuses: statusesByParentCategory.activeStaffStatuses,

        hasSchoolYears,
        hasStatuses,
        hasHouses,
        hasDepartments,
        hasSystemRole,
        isSchoolAdmin,
        isFetching,
        isCompleted,
        isSchoolPath,
        isSchoolChanging,
        updateSchoolMutation,
        onSelectSchool: handleSelectSchool,

        staffPropertiesFetching,
        studentPropertiesFetching,
        categoriesFetching,
      }}
    >
      {children}
    </SchoolContext.Provider>
  );
};
